import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, MapPin, GraduationCap, Bus, Sunrise, Palmtree, Gamepad2 } from "lucide-react";
import { PageHero } from "@/components/Decor";
import { ACTIVITES, ECOLES, HORAIRES_SOUTIEN, HORAIRES_STAGE, HORAIRES_MATIN, TARIFS_MATIN, TARIF_MATIN_UNITAIRE, formatXPF, TARIF_DEMI_JOURNEE, TARIF_TRAJET } from "@/lib/data";

function ServiceBlock({ id, icon: Icon, eyebrow, title, children }) {
  return (
    <section id={id} className="relative py-20 border-t border-ocean/5">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="sticky top-32">
              <div className="w-14 h-14 rounded-2xl bg-coral/10 text-coral flex items-center justify-center mb-5">
                <Icon size={26} />
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-3">{eyebrow}</div>
              <h2 className="font-display text-3xl sm:text-4xl text-ocean leading-tight">{title}</h2>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-6">{children}</div>
        </div>
      </div>
    </section>
  );
}

function HoraireCard({ horaires, title }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-ocean/5">
      <div className="flex items-center gap-2 mb-4">
        <Clock size={18} className="text-coral" />
        <h4 className="font-display text-lg text-ocean">{title}</h4>
      </div>
      <div className="space-y-2">
        {horaires.map((h) => (
          <div key={h.label} className="flex justify-between text-sm border-b border-ocean/5 pb-2 last:border-0">
            <span className="text-ocean/60">{h.label}</span>
            <span className="font-medium text-ocean">{h.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Nos services"
        title={<>Tout ce qu'il faut pour <em className="italic text-coral">les accompagner</em></>}
        subtitle="Soutien scolaire, ramassage, journées pédagogiques, stages de vacances et activités d'éveil — un service complet pensé pour les familles."
      >
        <div className="mt-10 flex flex-wrap justify-center gap-2 text-sm">
          {[
            { href: "#soutien", label: "Soutien" },
            { href: "#matin", label: "Accueil du matin" },
            { href: "#transport", label: "Transport" },
            { href: "#stages", label: "Stages vacances" },
            { href: "#activites", label: "Activités" },
          ].map((l) => (
            <a key={l.href} href={l.href} className="px-4 py-2 rounded-full bg-white border border-ocean/10 hover:bg-ocean hover:text-white transition-colors text-ocean/75">
              {l.label}
            </a>
          ))}
        </div>
      </PageHero>

      <ServiceBlock id="soutien" icon={GraduationCap} eyebrow="Service phare" title="Soutien scolaire quotidien">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
          <p className="text-ocean/70 leading-relaxed text-lg">
            Vous n'aurez plus à vous inquiéter pour votre enfant. Dès la sortie des cours, nous prenons le relais :
            nous le récupérons, l'aidons avec ses devoirs et révisions, et lui offrons un goûter.
            Il pourra ensuite s'amuser dans un environnement sécurisé et sous surveillance.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            <HoraireCard horaires={HORAIRES_SOUTIEN} title="Horaires" />
            <div className="bg-ocean text-white rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-coral mb-3">Tarification (sur la base de 3 semaines/mois)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between border-b border-white/15 pb-2 mb-1"><span className="text-white/70">Demi-journée</span><span className="font-medium">{formatXPF(TARIF_DEMI_JOURNEE)}</span></div>
                <div className="flex justify-between items-baseline">
                  <span className="text-white/70">2x / sem / mois</span>
                  <div className="text-right"><span className="font-medium">{formatXPF(20000)}</span><div className="text-[10px] text-white/50">soit {formatXPF(3333)} / demi-jour</div></div>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-white/70">3x / sem / mois</span>
                  <div className="text-right"><span className="font-medium">{formatXPF(27000)}</span><div className="text-[10px] text-white/50">soit {formatXPF(3000)} / demi-jour</div></div>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-white/70">4x / sem / mois</span>
                  <div className="text-right"><span className="font-medium">{formatXPF(33000)}</span><div className="text-[10px] text-white/50">soit {formatXPF(2750)} / demi-jour</div></div>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-white/70">5x / sem / mois</span>
                  <div className="text-right"><span className="font-medium">{formatXPF(38000)}</span><div className="text-[10px] text-white/50">soit {formatXPF(2533)} / demi-jour</div></div>
                </div>
              </div>
              <p className="mt-4 text-xs text-white/50">Goûter inclus dans le forfait mensuel.</p>
            </div>
          </div>
        </motion.div>
      </ServiceBlock>

      <ServiceBlock id="matin" icon={Sunrise} eyebrow="Nouveau — Dès 4h du matin" title="Accueil du matin">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
          <p className="text-ocean/70 leading-relaxed text-lg">
            Vous partez tôt au travail ? Nous accueillons votre enfant <strong>dès 4h du matin</strong>,
            lui offrons un <strong>petit-déjeuner</strong> et le déposons à son école.
            Un service pensé pour vous permettre de démarrer votre journée l'esprit tranquille.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            <HoraireCard horaires={HORAIRES_MATIN} title="Horaires" />
            <div className="bg-coral text-white rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-white/85 mb-3">Tarification (sur la base de 3 semaines/mois)</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between border-b border-white/15 pb-2 mb-1"><span className="text-white/80">Le jour (unitaire)</span><span className="font-medium">{formatXPF(TARIF_MATIN_UNITAIRE)}</span></div>
                {TARIFS_MATIN.map((t) => (
                  <div key={t.key} className="flex justify-between items-baseline">
                    <span className="text-white/80">{t.freq}</span>
                    <div className="text-right">
                      <span className="font-medium">{formatXPF(t.prix)}</span>
                      <div className="text-[10px] text-white/60">soit ~{formatXPF(t.per_day)}/jour</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-white/70">Petit-déjeuner et dépose à l'école inclus.</p>
            </div>
          </div>
        </motion.div>
      </ServiceBlock>

      <ServiceBlock id="transport" icon={Bus} eyebrow="Sérénité totale" title="Ramassage scolaire">
        <p className="text-ocean/70 leading-relaxed text-lg">
          Nous proposons un service de ramassage scolaire pour 8 écoles partenaires. Chaque après-midi,
          un ou plusieurs véhicules prennent en charge les élèves et les conduisent directement au centre.
        </p>

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl p-6 border border-ocean/5">
            <div className="flex items-center gap-2 mb-3"><MapPin size={18} className="text-coral" /><h4 className="font-display text-lg text-ocean">Sur Papara</h4></div>
            <ul className="text-sm text-ocean/70 space-y-1.5">
              {ECOLES.papara.map((e) => <li key={e} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-coral" />{e}</li>)}
            </ul>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ocean/5">
            <div className="flex items-center gap-2 mb-3"><MapPin size={18} className="text-coral" /><h4 className="font-display text-lg text-ocean">Sur Mataiea</h4></div>
            <ul className="text-sm text-ocean/70 space-y-1.5">
              {ECOLES.mataiea.map((e) => <li key={e} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-coral" />{e}</li>)}
            </ul>
          </div>
        </div>

        <div className="bg-ocean text-white rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-coral mb-3">Tarifs transport (sur la base de 3 semaines/mois)</div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
            <div className="flex justify-between sm:col-span-2 border-b border-white/10 pb-2"><span className="text-white/70">Le trajet (unitaire)</span><span className="font-medium">{formatXPF(TARIF_TRAJET)}</span></div>
            <div className="flex justify-between items-baseline">
              <span className="text-white/70">2x / sem / mois</span>
              <div className="text-right"><span className="font-medium">{formatXPF(1600)}</span><div className="text-[10px] text-white/50">soit {formatXPF(200)} / trajet</div></div>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-white/70">3x / sem / mois</span>
              <div className="text-right"><span className="font-medium">{formatXPF(2400)}</span><div className="text-[10px] text-white/50">soit {formatXPF(133)} / trajet</div></div>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-white/70">4x / sem / mois</span>
              <div className="text-right"><span className="font-medium">{formatXPF(3200)}</span><div className="text-[10px] text-white/50">soit {formatXPF(100)} / trajet</div></div>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-white/70">5x / sem / mois</span>
              <div className="text-right"><span className="font-medium">{formatXPF(3900)}</span><div className="text-[10px] text-white/50">soit {formatXPF(78)} / trajet</div></div>
            </div>
          </div>
          <p className="mt-4 text-xs text-white/50">Forfait mensuel — toutes écoles.</p>
        </div>
      </ServiceBlock>

      <ServiceBlock id="stages" icon={Palmtree} eyebrow="Pendant les vacances" title="Stages de vacances">
        <p className="text-ocean/70 leading-relaxed text-lg">
          Uniquement pendant les vacances scolaires, ce programme permet aux enfants de maintenir
          une dynamique d'apprentissage tout en s'amusant. Matinées dédiées au soutien, après-midis ludiques,
          et parfois des sorties sur la journée. <strong>Goûters et déjeuners inclus.</strong>
        </p>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl p-6 border border-ocean/5">
            <div className="text-xs uppercase tracking-widest text-coral mb-3">Non-abonnés au soutien</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ocean/60">Journée</span><span className="font-display text-lg text-ocean">{formatXPF(7000)}</span></div>
              <div className="flex justify-between"><span className="text-ocean/60">Semaine</span><span className="font-display text-lg text-ocean">{formatXPF(22000)}</span></div>
            </div>
          </div>
          <div className="bg-ocean text-white rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-coral mb-3">Abonnés au soutien (-23%)</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-white/70">Journée</span><span className="font-display text-lg">{formatXPF(6000)}</span></div>
              <div className="flex justify-between"><span className="text-white/70">Semaine</span><span className="font-display text-lg">{formatXPF(17000)}</span></div>
            </div>
          </div>
        </div>
      </ServiceBlock>

      <ServiceBlock id="activites" icon={Gamepad2} eyebrow="Après le soutien" title="Activités & Détente">
        <p className="text-ocean/70 leading-relaxed text-lg">
          Après la séance de soutien scolaire, votre enfant participe à des activités ludiques
          ou se détend en attendant votre arrivée. <strong>Objectif :</strong> sociabiliser et diminuer
          l'exposition aux écrans.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {ACTIVITES.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-ocean/5">
                <div className="w-10 h-10 rounded-xl bg-lagoon/30 text-ocean flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <span className="text-sm text-ocean/80">{a.label}</span>
              </div>
            );
          })}
        </div>
      </ServiceBlock>

      <section className="relative py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl text-ocean mb-5">Prêt à les inscrire ?</h2>
          <Link to="/inscription" className="btn-pill btn-accent inline-flex" data-testid="services-cta-inscription">
            Démarrer l'inscription <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
